import { FaInstagram } from "react-icons/fa";
import { FiFacebook, FiTwitter, FiYoutube } from "react-icons/fi";
import { FaLinkedinIn } from "react-icons/fa6";
import { ArrowUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../Authentication/AuthContext";

const Footer = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const handleScrollToTop = () => {
    const container = document.getElementById("main-scroll-container");
    if (container) {
      container.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  };

  const handleLoginNavigate = (endPoint, isLogin, role) => {
    const accessToken = localStorage.getItem("access");
    if (!accessToken && isLogin && !userRole) {
      toast.info("Please log in to perform this action.");
    } else if (role !== userRole && isLogin) {
      toast.info(`Please log in as a ${role} to perform this action.`);
    } else {
      navigate(`${endPoint}`);
    }
  };

  return (
    <footer className="bg-gray-900 text-white py-12 px-6 md:px-20">
      <div className="max-w-screen-xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-6 sm:gap-8">
        {/* Branding Section */}

        <div className="col-span-2 md:col-span-2 pr-0 md:pr-8">
          <img
            src="/upswap.png"
            alt="UpSwap Logo"
            className="w-32 h-auto mb-4"
          />
          <p className="text-sm text-gray-300 mb-4">
            UpSwap connects you with local businesses, exclusive deals, and
            community activities. Discover, book, and experience the best your
            area has to offer.
          </p>
          <div className="flex gap-4 text-xl text-gray-400 mt-2">
            <a
              href="https://www.facebook.com/upswap.official"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FiFacebook />
            </a>
            <a
              href="https://www.instagram.com/upswap.app/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaInstagram />
            </a>
            <a
              href="https://x.com/Upswapapp"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FiTwitter />
            </a>
            <a
              href="https://www.linkedin.com/company/upswap/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaLinkedinIn />
            </a>
            <a
              href="https://www.youtube.com/@UpSwap"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FiYoutube />
            </a>
          </div>

          {/* Store Badges */}
          <div className="flex gap-3 mt-6">
            <a
              href="https://apps.apple.com/us/app/upswap/id6478142973"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/Screenshot 2025-06-10 183311.png"
                alt="App Store"
                className="h-10 border-white border rounded-md cursor-pointer"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.spectralshifttech.upswap"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/Screenshot 2025-06-10 183323.png"
                alt="Google Play"
                className="h-10 border-white border rounded-md cursor-pointer"
              />
            </a>
          </div>
        </div>

        <div className="md:col-span-1">
          <h4 className="font-semibold text-white mb-4">Quick Links</h4>
          <ul className="text-sm text-gray-400 space-y-2">
            <li
              className="cursor-pointer"
              onClick={() => navigate("/LocalActivities")}
            >
              Browse Activities
            </li>
            <li
              className="cursor-pointer"
              onClick={() => navigate("/PopularDeals")}
            >
              Explore Deals
            </li>
            <li
              className="cursor-pointer"
              onClick={() => navigate("/PopularDeals")}
            >
              Find Vendors
            </li>
            <li
              className="cursor-pointer"
              onClick={() =>
                handleLoginNavigate("/services/browse", true, "user")
              }
            >
              Services
            </li>
            <li className="cursor-pointer" onClick={() => navigate("/")}>
              About Us
            </li>
          </ul>
        </div>

        {/* For Vendors */}
        <div className="md:col-span-1">
          <h4 className="font-semibold text-white mb-4">For Vendors</h4>
          <ul className="text-sm text-gray-400 space-y-2">
            <li className="cursor-pointer" onClick={() => navigate("/login")}>
              Join as Vendor
            </li>
            <li
              className="cursor-pointer"
              onClick={() => {
                handleLoginNavigate("/vendor/dashboard", true, "vendor");
              }}
            >
              Vendor Dashboard
            </li>
            <li
              className="cursor-pointer"
              onClick={() => {
                handleLoginNavigate("/vendor/deal/add", true, "vendor");
              }}
            >
              Create Deals
            </li>
            <li
              className="cursor-pointer"
              onClick={() =>
                handleLoginNavigate("/vendor/dashboard", true, "vendor")
              }
            >
              Analytics
            </li>
            <li
              className="cursor-pointer"
              onClick={() =>
                handleLoginNavigate("/vendor/dashboard", true, "vendor")
              }
            >
              Vendor Support
            </li>
          </ul>
        </div>

        {/* Support */}
        <div className="md:col-span-1">
          <h4 className="font-semibold text-white mb-4">Support</h4>
          <ul className="text-sm text-gray-400 space-y-2">
            <li className="cursor-pointer" onClick={() => navigate("/")}>
              Help Center
            </li>
            <li className="cursor-pointer" onClick={() => navigate("/")}>
              FAQs
            </li>
            <li className="cursor-pointer" onClick={() => navigate("/")}>
              Contact Us
            </li>
            <li className="cursor-pointer" onClick={() => navigate("/")}>
              Terms of Service
            </li>
            <li className="cursor-pointer" onClick={() => navigate("/")}>
              Privacy Policy
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="mt-4 border-t border-gray-800 pt-0 text-sm text-gray-400 flex justify-between items-center">
        <p>© 2025 UpSwap. All rights reserved.</p>
        <div
          onClick={handleScrollToTop}
          className="bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-full cursor-pointer"
        >
          <ArrowUp className="lucide lucide-arrow-up h-4 w-4 sm:h-5 sm:w-5" />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
